import React, { useContext, useEffect, useRef, useState } from "react";
import { DropDown } from "../../../../../components/dropDown/DropDown";
import { formatDate2, formatForInputValue, formatTimeForInputValue } from "../../../../../utils/dateFormats";
import { IClient, ISession } from "../../../../../types/dashboard";
import { addSessionValidate, ISessionErrors } from "./AddSessionValidate";
import { RepeatOptionsForm } from "./RepeatOptionsForm";
import { AppContext } from "../../../../../appContext";
import { dateToTimezoneAwareDatetime, parseTimezoneAwareDatetime } from "../../../../../utils/timezone";
import { SelectTime } from "../../../../../components/selectTime/SelectTime";
import './AddSession.css';
import { GoodScroll } from "../../../../desktopPage/GoodScroll";

interface IAddSessionProps{
    onClose: ()=>void;
    onSave: (data: ISession)=>void;
    onDelete: (data: ISession)=>void;
    onSaveDraft: ()=>void;
    clients: Array<IClient>;
    defaultClientId: string;
    sessionData: ISession;
}

interface IRepeatParams {
    repeat?: number, //0, count session to create each time
    repeatEvery?: number, //0, repeat interval multiplier
    repeatInterval?: string | "day" | "week" | "month"
}

export const AddSession = ({onClose, onSave, onDelete, clients, defaultClientId, sessionData}: IAddSessionProps)=>{
    const [title, setTitle] = useState('');
    const [date, setDate] = useState<Date>(new Date());
    const [timeFrom, setTimeFrom] = useState<Date>(new Date());
    const [timeTo, setTimeTo] = useState<Date>((()=>{
        const date = new Date();
        date.setUTCHours(date.getUTCHours() + 1);
        return date;
    })());
    const [selectedClient, setSelectedClient] = useState('');
    const [errors, setErrors] = useState<ISessionErrors>({});
    const [repeatParams, setRepeatParams] = useState<IRepeatParams>({})
    const [showTimeFromPicker, setShowTimeFromPicker] = useState(false);
    const [showTimeToPicker, setShowTimeToPicker] = useState(false);
    const timeFromRef = useRef<HTMLDivElement>(null);
    const timeToRef = useRef<HTMLDivElement>(null);

    const {cheat, translate} = useContext(AppContext);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (timeFromRef.current && !timeFromRef.current.contains(e.target as Node)) setShowTimeFromPicker(false);
            if (timeToRef.current && !timeToRef.current.contains(e.target as Node)) setShowTimeToPicker(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(()=>{
        setSelectedClient(clients.find(it=>it.id == defaultClientId)?.name || '')
    }, [defaultClientId, clients])

    useEffect(()=>{
        if (!sessionData){
            return;
        }
        setTitle(sessionData.title || '');
        
        // Handle both new timezone-aware format and legacy format during migration
        try {
            if (sessionData.startDatetime && sessionData.endDatetime) {
                // Use new timezone-aware format
                const startDate = parseTimezoneAwareDatetime(sessionData.startDatetime);
                const endDate = parseTimezoneAwareDatetime(sessionData.endDatetime);
                setDate(startDate);
                setTimeFrom(startDate);
                setTimeTo(endDate);
            } else if (sessionData.date && sessionData.dateTo) {
                // Fallback to legacy format
                setDate(new Date(sessionData.date));
                setTimeFrom(new Date(sessionData.date));
                setTimeTo(new Date(sessionData.dateTo));
            }
        } catch (e) {
            console.warn('Failed to parse session dates:', e);
            // Use defaults if parsing fails
            const now = new Date();
            const later = new Date(now.getTime() + 60 * 60 * 1000);
            setDate(now);
            setTimeFrom(now);
            setTimeTo(later);
        }
    }, [sessionData, clients]);

    const handleDelete = ()=>{
        onDelete(sessionData);
    }

    const handleSave = ()=>{
        const {isValid, errors} = addSessionValidate({title, date, timeFrom, timeTo, selectedClient});
        setErrors(errors);
        if (!isValid){
            return;
        }
        const fullDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), timeFrom.getHours(), timeFrom.getMinutes(), 0, 0);
        const fullDateTo = new Date(date.getFullYear(), date.getMonth(), date.getDate(), timeTo.getHours(), timeTo.getMinutes(), 0, 0);
        
        // Create session with new timezone-aware datetime format
        onSave({
            id: sessionData?.id || undefined,
            title: title,
            client: clients.find(it=>it.name == selectedClient)?.id,
            startDatetime: dateToTimezoneAwareDatetime(fullDate),
            endDatetime: dateToTimezoneAwareDatetime(fullDateTo),
            status: 'Upcoming',
            ...repeatParams
        });
    }

    const handleSaveDraft = ()=>{
        
    }

    return <div className="addSession">
        <div className="addSession_topMenu">
           {/* <BottomMenu selectedBottomMenuTabIndex={0} setSelectedBottomMenuTabIndex={()=>{}} bottomMenu={bottomMenu}></BottomMenu>
        */}</div>
        <div className="addSession_form">
            <div className="addSession_header">
                <div className="addSession_title">
                    {sessionData ? translate('session_edit_session') : translate('session_new_session')}
                </div>
                <div className="button addSession_icon addSession_close_icon" onClick={onClose}></div>
            </div>
            <div className="addSession_scrollWrap">
            <GoodScroll className="addSession_goodScroll">
            <div className="addSession_addTitle">
                <input className="addSession_addTitle_input" placeholder={translate('session_add_title')} value={title} onChange={(e)=>setTitle(e.target.value)}/>
            </div>
            <div className="addSession_inputs">
                <div className="addSession_formItem">
                    <div className="addSession_icon addSession_time_icon"></div>
                    <div>
                        <div className="addSession_formItem_content addSession_formItem_content--date">
                            <div className={`addSession_dateBlock ${errors.date ? 'addSession_error' : ''}`}>
                                {/*Wednesday, 13 August*/}
                                <div className="addSession_dateOutput">{date ? formatDate2(date, translate) : translate('session_select_date')}</div>
                                <input className="addSession_dateInput" type="date" value={formatForInputValue(date)} onChange={(e)=>{
                                    if (e.target.value) {
                                        const [year, month, day] = e.target.value.split('-').map(Number);
                                        const newDate = new Date(date || new Date());
                                        newDate.setFullYear(year, month - 1, day);
                                        setDate(newDate);
                                    }
                                }}></input>
                            </div>
                            <div ref={timeFromRef} className={`addSession_dateBlock addSession_timeField ${errors.timeFrom ? 'addSession_error' : ''}`} onClick={() => setShowTimeFromPicker(true)}>
                                <div className="addSession_dateOutput addSession_timeField_label">{formatTimeForInputValue(timeFrom)}</div>
                                {showTimeFromPicker && (
                                    <div className="addSession_timeField_popup" onClick={e => e.stopPropagation()}>
                                        <SelectTime selectedDate={timeFrom} onSelect={(d) => setTimeFrom(d)} defaultOpen />
                                    </div>
                                )}
                            </div>
                            <div ref={timeToRef} className={`addSession_dateBlock addSession_timeField ${errors.timeTo ? 'addSession_error' : ''}`} onClick={() => setShowTimeToPicker(true)}>
                                <div className="addSession_dateOutput addSession_timeField_label">{formatTimeForInputValue(timeTo)}</div>
                                {showTimeToPicker && (
                                    <div className="addSession_timeField_popup" onClick={e => e.stopPropagation()}>
                                        <SelectTime selectedDate={timeTo} onSelect={(d) => setTimeTo(d)} defaultOpen />
                                    </div>
                                )}
                            </div>
                        </div>
                        <RepeatOptionsForm onChange={setRepeatParams} repeatParams={repeatParams}></RepeatOptionsForm>
                    </div>
                </div>
                <div className="addSession_formItem">
                    <div className="addSession_icon addSession_user_icon"></div>
                    <div className="addSession_formItem_content">
                        <DropDown maxHeight={100} values={clients.map(it=>it.name)} onSelect={(selected)=>{setSelectedClient(clients[selected]?.name || '')}} selectedIndex={clients.findIndex(it=>it.name == selectedClient)}></DropDown>
                    </div>
                </div>
                {cheat.debug && <div className="addSession_formItem">
                    <div className="addSession_icon addSession_task_icon"></div>
                    <div className="addSession_formItem_content">
                        {translate('session_add_task')}
                    </div>
                </div>}
                {cheat.debug && <div className="addSession_formItem">
                    <div className="addSession_icon addSession_description_icon"></div>
                    <div className="addSession_formItem_content">
                        {translate('session_add_description')}
                    </div>
                </div>}
            </div>
            <div style={{height: 100+'px', flexShrink: 0}}>
                {/*drop down list comnensate, otherwise they can be cutted at bottom */}
            </div>
            </GoodScroll>
            </div>
            <div className="addSession_buttons">
                <div className="button addSession_draft" onClick={handleSaveDraft}>
                    {translate('session_save_draft')}
                </div>
                {sessionData && <div className="button saveButton addSession_save" onClick={handleDelete}>
                    {translate('session_delete')}
                </div>}
                <div className="button saveButton addSession_save" onClick={handleSave}>
                    {translate('session_save')}
                </div>
            </div>
        </div>
    </div>
}